import { useState } from 'react';
import { api, describeError, type VideoPlanView } from './api.js';

/** One line of a reel script: what the original says, and what Anthony says instead. */
interface Line {
  start?: number | undefined;
  end?: number | undefined;
  part?: string | undefined;
  original?: string | undefined;
  line?: string | undefined;
  onScreen?: string | undefined;
  film?: string | undefined;
}

/** A line from an older plan (written for an AI presenter). */
interface OldSegment {
  start?: number;
  end?: number;
  part?: string;
  shot?: string;
  visual?: string;
  voiceover?: string;
  onScreenText?: { text?: string } | string | null;
}

interface Plan {
  angle?: string;
  lengthSec?: number;
  script?: Line[];
  segments?: OldSegment[];
  screenRecordings?: string[];
  productFootage?: string[];
  filming?: string[];
  sound?: string;
  audio?: string;
  caption?: string;
  hashtags?: string[];
  whyThisShouldWork?: string;
}

const secs = (n: unknown) => (typeof n === 'number' ? `${Math.round(n * 10) / 10}s` : '?');

/** Old presenter plans read as scripts too (their voiceover is his line). */
function linesOf(plan: Plan): Line[] {
  if (plan.script?.length) return plan.script;
  return (plan.segments ?? []).map((s) => ({
    start: s.start,
    end: s.end,
    part: s.part,
    line: s.voiceover,
    onScreen: typeof s.onScreenText === 'string' ? s.onScreenText : s.onScreenText?.text,
    film: s.visual,
  }));
}

/**
 * Reel scripts: for each winning reel, a script for Anthony to film himself, adapted line by
 * line from what the original says, with what to show and how to film it.
 */
export function VideoPlans({
  plans,
  onChanged,
  onNotice,
}: {
  plans: VideoPlanView[];
  projectName?: string;
  /** Reload after a change. */
  onChanged: () => Promise<void>;
  onNotice: (text: string) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const remove = async (p: VideoPlanView) => {
    if (!window.confirm(`Remove the script “${p.title}”?`)) return;
    setBusy(p.id);
    try {
      await api.removePlan(p.project, p.id);
      onNotice(`Removed “${p.title}”.`);
      await onChanged();
    } catch (err) {
      onNotice(describeError(err));
    } finally {
      setBusy(null);
    }
  };
  const copy = (p: VideoPlanView, lines: Line[]) => {
    const text = lines
      .map((l) => l.line)
      .filter(Boolean)
      .join('\n');
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(p.id);
      setTimeout(() => setCopied(null), 2000);
    });
  };
  if (plans.length === 0) return null;
  return (
    <section className="panel">
      <h2 className="panel-title">Reel scripts ({plans.length})</h2>
      <p className="muted small">
        Scripts for you to film, adapted line by line from winning reels: their line, then yours,
        with what to show and how to shoot it. Tap “Write me a script” on a reel idea for more.
      </p>
      <ul className="idea-list">
        {plans.map((p) => {
          const plan = (p.plan ?? {}) as Plan;
          const lines = linesOf(plan);
          const recordings = plan.screenRecordings ?? plan.productFootage ?? [];
          const sound = plan.sound ?? plan.audio;
          const isOpen = open === p.id;
          return (
            <li key={p.id}>
              <div>
                <strong>{p.title}</strong>
                {plan.lengthSec ? <span className="muted small"> · {plan.lengthSec}s</span> : null}
              </div>
              {p.source && (
                <div className="inspiration small">
                  <span className="muted">Adapted from </span>
                  <a href={p.source.url} target="_blank" rel="noreferrer">
                    {p.source.label} ↗
                  </a>
                  <span className="muted"> · {p.source.numbers}</span>
                </div>
              )}
              {plan.angle && <div className="small">{plan.angle}</div>}
              <div className="idea-actions">
                <button
                  className="btn btn-small"
                  aria-expanded={isOpen}
                  onClick={() => setOpen(isOpen ? null : p.id)}
                >
                  {isOpen ? 'Hide the script' : 'Show the script'}
                </button>
                {lines.length > 0 && (
                  <button className="btn btn-small" onClick={() => copy(p, lines)}>
                    {copied === p.id ? 'Copied ✓' : 'Copy my lines'}
                  </button>
                )}
                <button
                  className="btn btn-small"
                  disabled={busy === p.id}
                  onClick={() => void remove(p)}
                >
                  {busy === p.id ? 'Removing…' : 'Remove'}
                </button>
              </div>
              {isOpen && (
                <div className="plan-script small">
                  <ol>
                    {lines.map((l, i) => (
                      <li key={i}>
                        <div className="muted">
                          {secs(l.start)}–{secs(l.end)}
                          {l.part ? ` · ${l.part}` : ''}
                        </div>
                        {l.original && (
                          <div className="muted">
                            They say: <em>“{l.original}”</em>
                          </div>
                        )}
                        {l.line && (
                          <div>
                            <strong>You say:</strong> “{l.line}”
                          </div>
                        )}
                        {l.onScreen && (
                          <div>
                            <span className="muted">On screen:</span> {l.onScreen}
                          </div>
                        )}
                        {l.film && (
                          <div>
                            <span className="muted">Film:</span> {l.film}
                          </div>
                        )}
                      </li>
                    ))}
                  </ol>
                  {recordings.length > 0 && (
                    <div className="plan-footage">
                      <strong>Screen recordings:</strong>
                      <ul>
                        {recordings.map((f) => (
                          <li key={f}>{f}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {plan.filming && plan.filming.length > 0 && (
                    <div>
                      <strong>Filming tips:</strong>
                      <ul>
                        {plan.filming.map((f) => (
                          <li key={f}>{f}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {sound && (
                    <p>
                      <span className="muted">Sound:</span> {sound}
                    </p>
                  )}
                  {plan.caption && (
                    <p>
                      <span className="muted">Caption:</span> {plan.caption}
                      {plan.hashtags?.length
                        ? ` ${plan.hashtags.map((h) => `#${h.replace(/^#/, '')}`).join(' ')}`
                        : ''}
                    </p>
                  )}
                  {plan.whyThisShouldWork && (
                    <p>
                      <span className="muted">Why it should work:</span> {plan.whyThisShouldWork}
                    </p>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
